import GenerarService from "../../services/GenerarService";
import documentoDocenteService from "../../services/documentoDocenteService";
import { runWithTiming } from "./utils/runWithTiming";

/* =======================
   Reglas de negocio
======================= */

/**
 * Determina si la sesión ya ha sido producida (finalizada).
 */
export const isProducido = (sesion) => {
  const estado = (sesion?.estado ?? "").toString().trim().toLowerCase();
  return estado === "producido";
};

/**
 * Determina si se puede iniciar el proceso de generación.
 * Ahora permite generar si NO está producido, incluso si ya existe un mongo_id parcial.
 */
export const canGenerate = (sesion, invalidPdfIds) => {
  // Permitimos generar si el estado es distinto a "producido" 
  // o si el ID está marcado como PDF inválido para reintentar.
  return !isProducido(sesion) || (sesion.id && invalidPdfIds.includes(sesion.id));
};

/**
 * La comparativa con el documento del docente solo aplica cuando la
 * sesión se genera a partir de un documento (llega con file_id).
 */
export const aplicaComparativa = (sesion) =>
  Boolean(sesion?.file_id ?? sesion?.fileId);

/* =======================
   Payload
======================= */

/**
 * Construye el objeto de datos para enviar al backend.
 * Se asegura de usar el ID de PostgreSQL correcto para que el backend reconozca la sesión.
 */
// export const buildPayload = (sesion) => {
//   const idPSQL = sesion?.id_PSQL ?? sesion?.id;
//   if (!idPSQL) {
//     throw new Error("Sesión inválida (id PostgreSQL faltante)");
//   }

//   // Estructura para sesiones SAE
//   if (sesion.tipo_sesion === "SAE") {
//     return {
//       sesiones_data: [{
//         id_PSQL: String(idPSQL),
//         tipo_sesion: "SAE",
//         nivel: sesion.nivel,
//         grado: Number(sesion.grado),
//         area_tematica: sesion.area_tematica,
//         tema: sesion.tema,
//         objetivo: sesion.objetivo,
//         subtipo_sesion: sesion.subtipo_sesion || "GENERAL",
//         sub_area_tematica: sesion.sub_area_tematica || "",
//         link_personalizado: "",
//       }],
//     };
//   }

//   // Estructura para sesiones SAC
//   if (sesion.tipo_sesion === "SAC") {
//     return {
//       sesiones_data: [{
//         id_PSQL: String(idPSQL),
//         tipo_sesion: "SAC",
//         nivel: sesion.nivel,
//         grado: Number(sesion.grado),
//         area_tematica: sesion.area_tematica,
//         tema: sesion.tema,
//         objetivo: sesion.objetivo,
//         unidad: sesion.unidad || "",
//         nro_unidad: Number(sesion.nro_unidad || 0),
//         situacion_significativa: sesion.situacion_significativa || "",
//         nro_sesion: Number(sesion.nro_sesion || 0),
//         contexto: "",
//         sigla_area_tematica: "",
//       }],
//     };
//   }

//   throw new Error("Tipo de sesión no soportado");
// };

export const buildPayload = (
    sesion
  ) => {
    const idPSQL =
      sesion?.id_PSQL ??
      sesion?.id_psql ??
      sesion?.idPsql ??
      sesion?.id;

    if (!idPSQL) {
      throw new Error(
        "Sesión inválida " +
        "(id PostgreSQL faltante)"
      );
    }

    const fileIdActual =
      sesion?.file_id ??
      sesion?.fileId ??
      null;

    // ==========================================================
    // SESIONES SAE
    // ==========================================================

    if (
      sesion.tipo_sesion ===
      "SAE"
    ) {
      return {
        sesiones_data: [
          {
            id_PSQL:
              String(idPSQL),

            tipo_sesion:
              "SAE",

            nivel:
              sesion.nivel,

            grado:
              Number(
                sesion.grado
              ),

            area_tematica:
              sesion.area_tematica,

            tema:
              sesion.tema,

            objetivo:
              sesion.objetivo,

            subtipo_sesion:
              sesion.subtipo_sesion ||
              "GENERAL",

            sub_area_tematica:
              sesion.sub_area_tematica ||
              "",

            link_personalizado:
              "",
          },
        ],
      };
    }

    // ==========================================================
    // SESIONES SAC
    // ==========================================================

    if (
      sesion.tipo_sesion ===
      "SAC"
    ) {
      const sesionData = {
        id_PSQL:
          String(idPSQL),

        tipo_sesion:
          "SAC",

        nivel:
          sesion.nivel,

        grado:
          Number(
            sesion.grado
          ),

        area_tematica:
          sesion.area_tematica,

        tema:
          sesion.tema,

        objetivo:
          sesion.objetivo,

        unidad:
          sesion.unidad ||
          "",

        nro_unidad:
          Number(
            sesion.nro_unidad ||
            0
          ),

        situacion_significativa:
          sesion
            .situacion_significativa ||
          "",

        nro_sesion:
          Number(
            sesion.nro_sesion ||
            0
          ),

        contexto:
          sesion.contexto ||
          "",

        sigla_area_tematica:
          sesion
            .sigla_area_tematica ||
          "",
      };

      /*
      * Solo se agrega file_id cuando realmente
      * existe un identificador válido.
      */
      if (fileIdActual) {
        sesionData.file_id =
          fileIdActual;
      }

      return {
        sesiones_data: [
          sesionData,
        ],
        estandares_ap:
          sesion.estandares_ap ??
          null,
      };
    }

    throw new Error(
      "Tipo de sesión no soportado"
    );
  };

/* =======================
   Secuencia de generación
======================= */

export const generarSesionCompleta = async ({
  sesion,
  updateProgress,
  setCurrentMongoStep,
  mongoByPostgresId,
  setMongoByPostgresId,
  onMongoCreated,
}) => {
  // Buscamos si ya existe un ID de mongo asociado para intentar reanudar
  let mongo =
    mongoByPostgresId?.[sesion.id] ||
    sesion.sesionGenerada_id || // Priorizamos el ID de mongo guardado en PSQL
    sesion.mongo_id ||
    null;

  const MONGO_PROGRESS_MAP = {
    inicio: 5,
    competencias: 10,
    prompt_1: 15,
    videos: 20,
    prompt_2: 25,
    prompt_3: 30,
    prompt_4: 35,
    prompt_5: 40,
    prompt_6: 50,
    prompt_7: 60,
    prompt_8: 70,
    prompt_9: 80,
    prompt_10: 90,
    final: 100,
  };

  const conComparativa = aplicaComparativa(sesion);

  if (conComparativa) {
    updateProgress("comparativa", "pendiente", 0);
  }

  /* ===== Paso 1: Generación de Contenido (Mongo) ===== */
  // Si no está terminada, ejecutamos el stream (que ahora soporta reanudación)
  if (!isProducido(sesion)) {
    const payload = buildPayload(sesion);
    const t0 = performance.now();

    updateProgress("mongo", "en_progreso", 0);

    try {
      const sessionId = await GenerarService.generarSesionStream({
        payload,
        tipoSesion: sesion.tipo_sesion,

        onProgress: ({ step }) => {
          const percent = MONGO_PROGRESS_MAP[step] ?? 0;
          updateProgress("mongo", "en_progreso", percent);
          setCurrentMongoStep(step);
        },

        onCompleted: () => {
          setCurrentMongoStep("final");
        },

        onError: (e) => {
          updateProgress("mongo", "error", 0);
          setCurrentMongoStep(null);
          throw e;
        },
      });

      const tiempo = performance.now() - t0;
      mongo = sessionId;

      updateProgress("mongo", "completado", 100, tiempo);
      setCurrentMongoStep("final");

      // Notificar creación o recuperación del ID
      onMongoCreated?.(mongo);

      // Actualizar estado local si existe la función
      if (setMongoByPostgresId) {
        setMongoByPostgresId(prev => ({
          ...prev,
          [sesion.id]: mongo,
        }));
      }
    } catch (error) {
      console.error("Error en flujo de stream:", error);
      throw error;
    }
  }

  /* ===== Paso 2: Generación de Documentos (Word, PDF, HTML) ===== */
  // Estos servicios en el backend también son idempotentes (si existen, no gastan créditos)

  await runWithTiming({
    step: "word",
    updateProgress,
    task: () => GenerarService.generarWord(mongo, sesion.tipo_sesion),
  });

  await runWithTiming({
    step: "pdf",
    updateProgress,
    task: () => GenerarService.generarPdf(mongo, sesion.tipo_sesion),
  });

  await runWithTiming({
    step: "html",
    updateProgress,
    task: () => GenerarService.generarHtml(mongo, sesion.tipo_sesion),
  });

  /* ===== Paso 3: Comparativa con el documento del docente ===== */
  // Paso extra al final: si falla, la sesión y sus archivos siguen siendo válidos
  // y la comparativa se puede reintentar desde «Ver comparativa».
  if (conComparativa) {
    try {
      await runWithTiming({
        step: "comparativa",
        updateProgress,
        task: () =>
          documentoDocenteService.generarComparativa(
            sesion.id_PSQL ?? sesion.id
          ),
      });
    } catch (error) {
      console.error("Error generando la comparativa:", error);
      updateProgress("comparativa", "error", 0);
    }
  }

  return mongo;
};
